import { useEffect, useMemo, useState } from 'react';
import { BrandWordmark } from '../components/BrandIdentity';

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

async function readJson(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Operazione non riuscita');
  return payload;
}

export default function RecoverBookingPublic({ token }) {
  const minDate = useMemo(() => localDateString(), []);
  const [date, setDate] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');
  const [mode, setMode] = useState('loading');
  const [externalUrl, setExternalUrl] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`/api/recover/resolve?token=${encodeURIComponent(token)}`).then(readJson)
      .then((payload) => {
        if (!active) return;
        setRestaurantName(payload.restaurantName || 'Ristorante');
        setMode(payload.bookingMode || 'native');
        setExternalUrl(payload.externalBookingUrl || '');
      })
      .catch((requestError) => {
        if (!active) return;
        setMode('error');
        setError(requestError.message);
      });
    return () => { active = false; };
  }, [token]);

  async function loadAvailability(event) {
    event.preventDefault();
    setError(''); setSelectedSlot(null); setLoading(true);
    try {
      const params = new URLSearchParams({ token, date, partySize: String(partySize) });
      const payload = await readJson(await fetch(`/api/recover/availability?${params}`));
      setRestaurantName(payload.restaurantName || restaurantName || 'Ristorante');
      setSlots(payload.slots || []);
      if (!payload.slots?.length) setError('Nessun orario disponibile per questa data.');
    } catch (requestError) { setSlots([]); setError(requestError.message); }
    finally { setLoading(false); }
  }

  async function confirmBooking(event) {
    event.preventDefault();
    if (!selectedSlot) return;
    setError(''); setLoading(true);
    try {
      const payload = await readJson(await fetch('/api/recover/confirm', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, date, shiftId: selectedSlot.shiftId, startTime: selectedSlot.startTime, partySize, firstName: form.firstName, lastName: form.lastName, notes: form.notes }),
      }));
      setBooking(payload.booking);
    } catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }

  if (mode === 'loading') return <BookingShell><div className="booking-loading"><span /><p>Prepariamo la prenotazione…</p></div></BookingShell>;
  if (mode === 'external' && externalUrl) {
    return <BookingShell><div className="booking-success-icon">↗</div><p className="eyebrow">{restaurantName}</p><h1>Continua sul sito del ristorante</h1><p className="booking-lead">Abbiamo preparato il collegamento ufficiale per completare la prenotazione.</p><a className="button button-large full" href={externalUrl} rel="noreferrer">Apri le disponibilità ↗</a><p className="booking-privacy">RistoDaily registra soltanto l’apertura del collegamento per misurare il recupero della chiamata.</p></BookingShell>;
  }
  if (booking) {
    return <BookingShell><div className="booking-success-icon success">✓</div><p className="eyebrow">PRENOTAZIONE CONFERMATA</p><h1>{booking.restaurantName}</h1><p className="booking-lead">{booking.firstName}, ti aspettiamo <strong>{new Date(`${booking.date}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</strong> alle <strong>{booking.startTime}</strong>, per {booking.partySize} {booking.partySize === 1 ? 'persona' : 'persone'}.</p>{booking.manageUrl ? <a className="button full" href={booking.manageUrl}>Gestisci la prenotazione</a> : <div className="booking-note">Puoi chiudere questa pagina. Il ristorante ha già ricevuto la prenotazione.</div>}</BookingShell>;
  }
  if (mode === 'error') return <BookingShell><div className="booking-success-icon error">!</div><h1>Questo link non è più disponibile</h1><p className="booking-lead">{error || 'Potrebbe essere scaduto o già utilizzato. Richiama il ristorante per completare la prenotazione.'}</p></BookingShell>;

  return (
    <BookingShell>
      <p className="eyebrow">{restaurantName || 'RISTODAILY RECOVER'}</p><h1>Completa la prenotazione</h1><p className="booking-lead">Scegli data e orario. Il numero usato per la chiamata verrà associato automaticamente.</p>
      <form className="booking-form" onSubmit={loadAvailability}>
        <div className="form-row"><label>Data<input type="date" min={minDate} required value={date} onChange={(event) => setDate(event.target.value)} /></label><label>Persone<input type="number" min="1" max="50" required value={partySize} onChange={(event) => setPartySize(Number(event.target.value))} /></label></div>
        <button className="button full" disabled={loading || !date}>{loading && !selectedSlot ? 'Cerco gli orari…' : 'Mostra disponibilità'}</button>
      </form>
      {!!slots.length && <div className="slot-section"><strong>Orari disponibili</strong><div className="slot-grid">{slots.map((slot) => { const selected = selectedSlot?.shiftId === slot.shiftId && selectedSlot?.startTime === slot.startTime; return <button className={selected ? 'selected' : ''} key={`${slot.shiftId}-${slot.startTime}`} onClick={() => { setSelectedSlot(slot); setError(''); }}><b>{slot.startTime}</b><small>{slot.shiftName}</small></button>; })}</div></div>}
      {selectedSlot && <form className="booking-form customer-form" onSubmit={confirmBooking}><strong>I tuoi dati</strong><div className="form-row"><label>Nome<input required maxLength="80" autoComplete="given-name" value={form.firstName} onChange={(event) => setForm((current) => ({ ...current, firstName: event.target.value }))} /></label><label>Cognome<input required maxLength="80" autoComplete="family-name" value={form.lastName} onChange={(event) => setForm((current) => ({ ...current, lastName: event.target.value }))} /></label></div><label>Note <small>(facoltative)</small><textarea maxLength="500" rows="3" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></label><button className="button full" disabled={loading}>{loading ? 'Conferma in corso…' : `Conferma per le ${selectedSlot.startTime}`}</button></form>}
      {error && <div className="form-alert error" role="alert">{error}</div>}
      <p className="booking-privacy">Il numero della chiamata viene usato solo per questa richiesta e per le comunicazioni di servizio collegate. Nessun messaggio promozionale.</p>
    </BookingShell>
  );
}

function BookingShell({ children }) {
  return <main className="booking-page"><div className="booking-orb one" /><div className="booking-orb two" /><section className="booking-card glass-card"><div className="booking-brand"><BrandWordmark /><span className="booking-product">Prenota</span></div>{children}</section></main>;
}
